/**
 * Wave 3 · 24 #10 — monthly collections forecast (pure helpers for GET /invoicing/collection-forecast).
 * Series, bottom to top: overdue (raised, past the date the client usually pays — chase them), raised (unpaid
 * invoices, on their due date or submitted + the client's usual days to pay), ready to bill (work done, billed today)
 * and planned (milestones with a planned invoicing / execution date).
 */
import { formatNumber, toNumber } from '@/lib/format';

const num = (value) => toNumber(value) ?? 0;

// Theme tokens only (status colours + the foundation's chart-2 indigo), so every theme — dark_brand included — keeps
// the four series apart without per-theme overrides here.
export const COLLECTION_SERIES = [
  { key: 'overdue', label: 'Overdue — chase', className: 'bg-status-red' },
  { key: 'raised', label: 'Invoiced, due', className: 'bg-chart-2' },
  { key: 'ready', label: 'Ready to bill', className: 'bg-status-green' },
  { key: 'planned', label: 'Planned milestones', className: 'bg-status-blue' },
];

/** Chart model: bar heights as % of the biggest month, with the per-series segments (only those > 0). */
export function collectionBars(months) {
  const list = Array.isArray(months) ? months : [];
  const max = Math.max(0, ...list.map((month) => num(month.total)));
  return list.map((month) => ({
    ...month,
    heightPct: max > 0 ? (num(month.total) / max) * 100 : 0,
    segments: COLLECTION_SERIES
      .map((series) => ({ ...series, value: num(month[series.key]) }))
      .filter((segment) => segment.value > 0),
  }));
}

export function seriesTotals(months) {
  return Object.fromEntries(COLLECTION_SERIES.map((series) => [series.key, (months || []).reduce((sum, month) => sum + num(month[series.key]), 0)]));
}

/** 676795 → '677K', 8629687 → '8.6M', 950 → '950' (bar labels). */
export function compactAed(value) {
  const number = num(value);
  if (Math.abs(number) >= 1e6) return `${formatNumber(number / 1e6, { maxDecimals: 1 })}M`;
  if (Math.abs(number) >= 1e3) return `${formatNumber(number / 1e3, { decimals: 0 })}K`;
  return formatNumber(number, { decimals: 0 });
}

/** The basis line: which days-to-pay the forecast used. */
export function basisText(basis) {
  if (!basis) return '';
  if (basis.using_default) {
    return `Days to pay: ${basis.default_days} d (default) — no paid invoices have both a submitted and a paid date yet. Recording “Paid on” makes this sharper.`;
  }
  const parts = [];
  if (basis.company_days) parts.push(`company median ${basis.company_days} d from ${formatNumber(basis.samples)} paid invoices`);
  if (basis.clients_with_history) parts.push(`each client's own median for ${formatNumber(basis.clients_with_history)} clients`);
  return `Days to pay: ${parts.join('; ')} — others use ${basis.company_days || basis.default_days} d.`;
}
